import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ConnectionState } from 'livekit-client';
import CallSession from '../components/CallSession';
import useCallStream from '../hooks/useCallStream';
import useNow from '../hooks/useNow';
import { resolveBackendPath } from '../utils/backendUrl';
import { normalizeLivekitUrl } from '../utils/livekitUrl';
import type { Call, CallEvent } from '../types';
import { useOperatorConfig } from '../hooks/useOperatorConfig';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { useAuth } from '../hooks/useAuth';
import { Kbd, ReticleIcon, formatWait, waitStatus } from '../components/primitives';

interface SessionState {
  call: Call;
  token: string;
  expiresAt: string;
  serverUrl: string;
}

let audioContext: AudioContext | null = null;
function playNotificationTone() {
  try {
    audioContext = audioContext ?? new AudioContext();
    const ctx = audioContext;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(660, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
  } catch (error) {
    console.warn('Unable to play notification tone', error);
  }
}

export default function CallsPage() {
  const { config } = useOperatorConfig();
  const { session: authSession } = useAuth();
  const operatorToken = authSession?.token ?? '';
  const livekitUrl = useMemo(() => normalizeLivekitUrl(config.livekitUrl), [config.livekitUrl]);
  const [calls, setCalls] = useState<Map<string, Call>>(() => new Map());
  const [joiningCallId, setJoiningCallId] = useState<string | null>(null);
  const [callSession, setCallSession] = useState<SessionState | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
  const [dismissing, setDismissing] = useState<Set<string>>(() => new Set());
  const { setBackendStatus, setLivekitStatus } = useConnectionStatus();
  const now = useNow(1000);
  const streamUrl = useMemo(
    () => resolveBackendPath('/api/calls/stream', config.backendUrl),
    [config.backendUrl],
  );
  const resolveApi = useCallback(
    (path: string) => resolveBackendPath(path, config.backendUrl),
    [config.backendUrl],
  );

  const configWarnings = useMemo(() => {
    const items: string[] = [];
    if (!livekitUrl) {
      items.push('LiveKit server URL is required to connect to active calls.');
    }
    if (!operatorToken) {
      items.push('Sign in to the operator console to join or end calls.');
    }
    return items;
  }, [operatorToken, livekitUrl]);

  const sortedCalls = useMemo(() => {
    return Array.from(calls.values()).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
  }, [calls]);

  useEffect(() => {
    const count = sortedCalls.length;
    const consoleTitle = 'A.R.E.S — Operator Console';
    document.title = count > 0 ? `(${count}) ${consoleTitle}` : consoleTitle;
  }, [sortedCalls]);

  const updateLivekitConnection = useCallback(
    (state: ConnectionState) => {
      setConnectionState(state);
      if (state === ConnectionState.Connected) {
        setLivekitStatus('connected');
      } else if (state === ConnectionState.Connecting || state === ConnectionState.Reconnecting) {
        setLivekitStatus('connecting');
      } else {
        setLivekitStatus('disconnected');
      }
    },
    [setLivekitStatus],
  );

  const handleStreamEvent = useCallback(
    (event: CallEvent) => {
      if (event.type === 'existing_call' || event.type === 'new_call') {
        const payload: Call = {
          ...event.data,
          displayName: event.data.displayName ?? 'M400',
          agentName: event.data.agentName ?? null,
          deploymentLocation: event.data.deploymentLocation ?? null,
          deploymentLocationId: event.data.deploymentLocationId ?? null,
        };
        setCalls((prev) => {
          const next = new Map(prev);
          const alreadyHad = next.has(payload.callId);
          next.set(payload.callId, payload);
          if (event.type === 'new_call' && !alreadyHad) {
            const subject = payload.agentName || payload.displayName || 'Caller';
            const locationSuffix = payload.deploymentLocation ? ` at ${payload.deploymentLocation}` : '';
            playNotificationTone();
            toast.success(`${subject}${locationSuffix} is requesting support`);
          }
          return next;
        });
      } else if (event.type === 'ended') {
        setCalls((prev) => {
          const next = new Map(prev);
          next.delete(event.data.callId);
          return next;
        });
        setDismissing((prev) => {
          const next = new Set(prev);
          next.delete(event.data.callId);
          return next;
        });
        if (callSession?.call.callId === event.data.callId) {
          toast('Call ended by device');
          setCallSession(null);
          updateLivekitConnection(ConnectionState.Disconnected);
        }
      }
    },
    [callSession, updateLivekitConnection],
  );

  useCallStream(handleStreamEvent, streamUrl, {
    onStatusChange: setBackendStatus,
  });

  const joinCall = useCallback(
    async (call: Call) => {
      if (!operatorToken) {
        toast.error('Sign in to the operator console before joining calls.');
        return;
      }
      if (!livekitUrl) {
        toast.error('LiveKit URL is not configured.');
        return;
      }
      setJoiningCallId(call.callId);
      updateLivekitConnection(ConnectionState.Connecting);
      try {
        const response = await fetch(resolveApi(`/api/calls/${call.callId}/token?role=agent`), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${operatorToken}`,
          },
        });
        if (!response.ok) {
          const detail = await response.text();
          throw new Error(detail || 'Unable to fetch token');
        }
        const data = (await response.json()) as { token: string; roomName: string; expiresAt: string };
        setCallSession({ call, token: data.token, expiresAt: data.expiresAt, serverUrl: livekitUrl });
      } catch (error) {
        console.error(error);
        toast.error(error instanceof Error ? error.message : 'Failed to join call');
        updateLivekitConnection(ConnectionState.Disconnected);
      } finally {
        setJoiningCallId(null);
      }
    },
    [operatorToken, livekitUrl, resolveApi, updateLivekitConnection],
  );

  const leaveSession = useCallback(() => {
    setCallSession(null);
    updateLivekitConnection(ConnectionState.Disconnected);
  }, [updateLivekitConnection]);

  const dismissCall = useCallback(
    async (call: Call) => {
      if (!operatorToken) {
        toast.error('Sign in to the operator console before ending calls.');
        return;
      }
      setDismissing((prev) => {
        const next = new Set(prev);
        next.add(call.callId);
        return next;
      });
      try {
        const response = await fetch(resolveApi(`/api/calls/${call.callId}/end`), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${operatorToken}`,
          },
        });
        if (!response.ok) {
          const detail = await response.text();
          throw new Error(detail || 'Unable to dismiss call');
        }
      } catch (error) {
        console.error(error);
        toast.error(error instanceof Error ? error.message : 'Failed to dismiss call');
      } finally {
        setDismissing((prev) => {
          const next = new Set(prev);
          next.delete(call.callId);
          return next;
        });
      }
    },
    [operatorToken, resolveApi],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (event.key.toLowerCase() === 'j') {
        event.preventDefault();
        if (!callSession) {
          const [firstCall] = sortedCalls;
          if (firstCall) {
            void joinCall(firstCall);
          }
        }
      }
      if (event.key.toLowerCase() === 'l') {
        event.preventDefault();
        if (callSession) {
          leaveSession();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sortedCalls, callSession, joinCall, leaveSession]);

  return (
    <div className="calls-page">
      {configWarnings.length > 0 && (
        <div className="config-banner" role="status">
          <div className="config-banner__heading">Configuration required</div>
          <ul>
            {configWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="triage-panel" aria-label="Incoming calls">
        <header className="triage-panel__header">
          <div>
            <h2 className="triage-panel__title">Incoming</h2>
            <p className="triage-panel__sub">
              {sortedCalls.length === 0
                ? 'Devices will appear here when they request support.'
                : `${sortedCalls.length} waiting`}
            </p>
          </div>
          {sortedCalls.length > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Kbd>J</Kbd>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>claim oldest</span>
            </div>
          )}
        </header>

        <div className="triage-list">
          {sortedCalls.length === 0 && (
            <div className="empty" style={{ padding: '36px 12px' }}>
              <div className="empty__icon">
                <ReticleIcon size={64} stroke={1.1} />
              </div>
              <h3 className="empty__title">All clear</h3>
              <p className="empty__body">No active calls right now. The triage list will update automatically.</p>
            </div>
          )}
          {sortedCalls.map((call) => {
            const ageMs = now.getTime() - new Date(call.createdAt).getTime();
            const status = waitStatus(ageMs);
            const badgeClass =
              status === 'fresh' ? 'wait-badge' : `wait-badge wait-badge--${status}`;
            const isJoining = joiningCallId === call.callId;
            const isDismissing = dismissing.has(call.callId);
            const isActiveSession = callSession?.call.callId === call.callId;
            const headerLabel = call.agentName?.trim() || call.displayName?.trim() || 'M400';
            const deviceTail = call.deviceId ? call.deviceId.slice(-4) : '';

            return (
              <article
                key={call.callId}
                className={`call-card${isActiveSession ? ' is-active' : ''}`}
                aria-label={`Call from ${headerLabel}, waiting ${formatWait(ageMs)}`}
              >
                <header className="call-card__header">
                  <h3 className="call-card__title">{headerLabel}</h3>
                  <span className={badgeClass}>{formatWait(ageMs)}</span>
                </header>

                <div className="call-card__meta">
                  {call.deploymentLocation && (
                    <div className="call-card__meta-row">
                      <span className="call-card__meta-label">Location</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{call.deploymentLocation}</span>
                    </div>
                  )}
                  <div className="call-card__meta-row">
                    <span className="call-card__meta-label">Device</span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {call.displayName}
                      {deviceTail && (
                        <span className="call-card__meta-value" style={{ marginLeft: 6 }}>
                          …{deviceTail}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="call-card__meta-row">
                    <span className="call-card__meta-label">Room</span>
                    <span className="call-card__meta-value">{call.roomName}</span>
                  </div>
                </div>

                <div className="call-card__actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => void joinCall(call)}
                    disabled={isJoining || isActiveSession}
                    aria-busy={isJoining || undefined}
                  >
                    {isActiveSession ? 'In call' : isJoining ? 'Connecting…' : 'Claim'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--destructive"
                    onClick={() => void dismissCall(call)}
                    disabled={isDismissing}
                  >
                    {isDismissing ? 'Ending…' : 'Dismiss'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="cockpit-panel" aria-label="Live session">
        <header className="cockpit-panel__header">
          <div>
            <h2 className="cockpit-panel__title">Live session</h2>
            <p className="cockpit-panel__sub">
              {callSession ? `Room ${callSession.call.roomName}` : 'Claim a call to start assisting.'}
            </p>
          </div>
          {callSession && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Kbd>L</Kbd>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>leave</span>
            </div>
          )}
        </header>

        <div className={`cockpit-panel__body${callSession ? ' cockpit-panel__body--active' : ''}`}>
          {callSession ? (
            <CallSession
              call={callSession.call}
              token={callSession.token}
              serverUrl={callSession.serverUrl}
              onLeave={leaveSession}
              onConnectionStateChange={updateLivekitConnection}
            />
          ) : (
            <div className="cockpit-empty">
              <div>
                <div className="empty__icon" style={{ marginBottom: 14 }}>
                  <ReticleIcon size={72} stroke={1.1} />
                </div>
                <h3 className="empty__title">Waiting to claim</h3>
                <p className="empty__body" style={{ marginInline: 'auto' }}>
                  Remote video appears here once you claim a call. Press <Kbd>J</Kbd> to claim the oldest one.
                </p>
              </div>
            </div>
          )}
          {callSession && connectionState === ConnectionState.Reconnecting && (
            <div className="reconnect-banner" role="status">
              <span className="pill__dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
              Attempting to reconnect…
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
